import { VideoRemove } from 'iconsax-react';
import { type ReactEventHandler, useLayoutEffect, useRef, useState, type VideoHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import PlayIcon from '@/assets/images/icons/play.svg?react';
import { calculateImageDimension } from '@/components/common/media-content/calculate-image-dimension';
import { SafeImage } from '@/components/common/safe-image';
import { useSafeMediaUrl } from '@/hooks/use-safe-media-url';
import useUserPlatform from '@/hooks/use-user-platform';
import { getPosterUrl, getPreviewableUrl } from '@/utils/media-url';
import cn from '@/utils/merge-class-names';
import { canParseAsUrl } from '@/utils/parse-url';
import { formatSecond } from '@/utils/second-to-time-string';

type VideoProps = Omit<VideoHTMLAttributes<HTMLVideoElement>, 'poster'> & {
  poster?: string | boolean | undefined;
  isInView?: boolean;
  respectRatio?: boolean;
};

/**
 * Hack for iOS devices to show video thumbnail
 */
export default function VideoViewer({
  preload = 'metadata',
  className,
  playsInline,
  src,
  controls,
  onLoadedMetadata,
  onError,
  poster = true,
  autoPlay = true,
  isInView = true,
  respectRatio = true,
  ...props
}: VideoProps) {
  const { t } = useTranslation();
  const { isIOS, isDesktop } = useUserPlatform();
  const { src: safeSrc } = useSafeMediaUrl(src);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [dimension, setDimension] = useState<[number | null, number | null, number | null]>([null, null, null]);
  const [duration, setDuration] = useState(0);
  const { value: hasPoster, setFalse: setHasNoPoster } = useBoolean(Boolean(poster));
  const { value: hasError, setTrue: setHasError } = useBoolean(false);

  const posterUrl = typeof poster === 'string' ? poster : poster ? getPosterUrl(safeSrc) : undefined;

  const handleLoadedMetadata: ReactEventHandler<HTMLVideoElement> = (event) => {
    const videoElement = event.target as HTMLVideoElement;
    const { duration, videoWidth, videoHeight } = videoElement;
    setDuration(duration);
    setDimension(calculateImageDimension(videoWidth, videoHeight));
    onLoadedMetadata?.(event);
  };

  const handlePlay = () => {
    videoRef.current?.play().catch(console.warn);
  };

  useLayoutEffect(() => {
    if (!posterUrl || !canParseAsUrl(posterUrl) || posterUrl.startsWith('blob:')) {
      setHasNoPoster();
      return;
    }

    const { searchParams } = new URL(posterUrl);
    if (searchParams.has('h')) {
      const width = Number(searchParams.get('w') || 0);
      const height = Number(searchParams.get('h') || 0);
      setDimension(
        calculateImageDimension(width, height, {
          maxWidth: isDesktop ? 380 : 0.45,
          maxHeight: isDesktop ? 0.4 : 0.25,
        }),
      );
    } else {
      setHasNoPoster();
    }
    if (searchParams.has('t')) {
      const time = Number(searchParams.get('t') || 0);
      setDuration(time);
    }
  }, [posterUrl, isDesktop, setHasNoPoster]);

  if (!isInView) {
    return null;
  }

  if (hasError) {
    return (
      <div className={cn('p-2 flex flex-center bg-neutral-0', className)}>
        <VideoRemove
          variant='Outline'
          className='size-8 text-neutral-300'
        />
      </div>
    );
  }

  if (!autoPlay && hasPoster && posterUrl) {
    return (
      <div className={cn('relative', { 'size-full': !respectRatio })}>
        <SafeImage
          src={posterUrl}
          style={
            respectRatio
              ? {
                  width: dimension[0] ?? '100%',
                  height: dimension[1] ?? (isDesktop ? '480px' : '194px'),
                  aspectRatio: dimension[2] ?? '16/9',
                }
              : undefined
          }
          className={cn('bg-cover', { 'size-full': !respectRatio }, className)}
          onImageError={() => setHasNoPoster()}
        />

        <PlayIndicator
          duration={duration}
          onClick={handlePlay}
        />
      </div>
    );
  }

  return (
    <div className='relative size-full flex flex-col flex-center'>
      <video
        ref={videoRef}
        className={className}
        playsInline={isIOS || playsInline}
        src={isIOS ? getPreviewableUrl(safeSrc) : safeSrc}
        controls={controls}
        poster={posterUrl}
        autoPlay={autoPlay}
        onError={(event) => {
          onError?.(event);
          setHasError();
        }}
        onLoadedMetadata={handleLoadedMetadata}
        {...props}>
        {t('errors.video-not-supported')}
      </video>
    </div>
  );
}

interface PlayIndicatorProps {
  duration: number;
  onClick: () => void;
}

function PlayIndicator({ duration, onClick }: PlayIndicatorProps) {
  return (
    <div className='z-10 absolute start-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-neutral-0 flex flex-col items-center'>
      <button
        type='button'
        onClick={() => onClick()}
        className='btn btn-ghost btn-sm btn-circle'>
        <PlayIcon className='size-8 drop-shadow' />
      </button>

      {duration > 0 && <span className='font-light text-shadow'>{formatSecond(duration)}</span>}
    </div>
  );
}
